import { useEffect, useLayoutEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageSquarePlus, Lock, Loader2 } from "lucide-react";

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  const rel = s < 60 ? "just now"
    : s < 3600 ? `${Math.floor(s / 60)}m ago`
    : s < 86400 ? `${Math.floor(s / 3600)}h ago`
    : s < 2592000 ? `${Math.floor(s / 86400)}d ago`
    : `${Math.floor(s / 2592000)}mo ago`;
  return `${rel} · ${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

// A note body that collapses to a 2-line preview with "Show more" when it overflows
// (used for long dismantler notes so they never bury the fields below).
function ClampBody({ text }) {
  const ref = useRef(null);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setOverflowing(el.scrollHeight - el.clientHeight > 2);
  }, [text]);
  return (
    <div>
      <p ref={ref} className={`text-sm text-slate-800 whitespace-pre-wrap ${expanded ? "" : "line-clamp-2"}`}>{text}</p>
      {(overflowing || expanded) && (
        <button data-testid="notes-log-showmore" onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 text-[11px] font-semibold text-[#B01E28] hover:underline">
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

// Unified APPEND-ONLY notes log — locked, timestamped, attributed entries, newest on top.
// You ADD notes; you never edit or delete old ones (a correction is a new note below).
export function NotesLog({ recordType, recordId, canAdd = false, clampLong = false }) {
  const [notes, setNotes] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/record-notes/${recordType}/${recordId}`); setNotes(data.notes || []); }
    catch { setNotes([]); }
  };
  useEffect(() => { if (recordId) load(); /* eslint-disable-next-line */ }, [recordType, recordId]);

  const add = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/record-notes/${recordType}/${recordId}`, { body });
      setNotes((n) => [data.note, ...(n || [])]);
      setDraft("");
      toast.success("Note added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`notes-log-${recordType}`}>
      {canAdd && (
        <div className="mb-3" data-testid="notes-log-add">
          <Textarea data-testid="notes-log-input" value={draft} onChange={(e) => setDraft(e.target.value)}
            onFocus={(e) => { const el = e.target; setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 300); }}
            placeholder="Add a note… (permanent, timestamped, attributed — old notes are never changed)"
            className="rounded-lg border-slate-200 text-sm" rows={2} />
          <div className="flex justify-end mt-1.5">
            <Button data-testid="notes-log-submit" size="sm" onClick={add} disabled={busy || !draft.trim()}
              className="rounded-lg bg-[#1F1F1F] hover:bg-black text-white h-8">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><MessageSquarePlus className="w-3.5 h-3.5 mr-1.5" />Add note</>}
            </Button>
          </div>
        </div>
      )}

      {notes === null ? (
        <div className="text-sm text-slate-400 py-3 flex items-center gap-2 font-mono"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="notes-log-empty">No notes yet.</p>
      ) : (
        <div className="space-y-2" data-testid="notes-log-list">
          {notes.map((n) => (
            <div key={n.id} data-testid={`notes-log-entry-${n.id}`}
              className="border border-slate-100 rounded-lg p-3 bg-slate-50/60">
              {clampLong ? <ClampBody text={n.body} /> : <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.body}</p>}
              <div className="flex items-center gap-1.5 mt-1.5 text-[11px] font-mono text-slate-500">
                <Lock className="w-3 h-3" />
                <span className="font-semibold text-slate-600">{n.by_name}</span>
                {n.by && n.by !== n.by_name && <span className="text-slate-400">({n.by})</span>}
                <span>· {when(n.at)}</span>
                {n.legacy && <span className="text-amber-600">· migrated</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
